import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setBeltDefectsSelectedDate, setBeltDefectsSelectedDefectHealth } from '@/store/slices/assetStudioSlice';
import BeltHealthTrend from './BottomSection/BeltHealthTrend';
import { TopSection } from './TopSection/TopSection';
import mockData from './mock-data.json';

export function BeltDefects() {
  const dispatch = useDispatch();
  const lastInspectionDate = mockData.inspectionDates[mockData.inspectionDates.length - 1];
  const [selectedDate, setSelectedDate] = useState<string>(lastInspectionDate);
  const [selectedDefectId, setSelectedDefectId] = useState<string>('');
  const [hasInitialized, setHasInitialized] = useState(false);
  const [healthFilters, setHealthFilters] = useState<Set<string>>(new Set(['critical', 'alarm', 'monitor', 'healthy']));

  // Transform defects data for belt map based on selected date
  const beltDefects = useMemo(() => {
    // Get the date string for comparison
    const dateStr = selectedDate.split('T')[0];

    // First, collect ALL defects for the date (before filtering)
    const allDefectsForDate: any[] = [];

    mockData.defects.forEach((defect) => {
      // Get the severity for this defect on the selected date
      const historicalData = mockData.historicalData[defect.id as keyof typeof mockData.historicalData];

      if (!historicalData || historicalData.length === 0) {
        // If no historical data at all, don't show this defect
        return;
      }

      // Check if this defect exists on the selected date
      const dateEntry = historicalData.find((h) => h.date === dateStr);
      if (!dateEntry) {
        // This defect doesn't exist on this date, skip it
        return;
      }

      // Add ALL defects for grouping purposes
      allDefectsForDate.push({
        ...defect,
        severity: dateEntry.severity,
      });
    });

    // Sort ALL defects by position (before filtering)
    allDefectsForDate.sort((a, b) => a.position - b.position);

    // Group items that would visually overlap based on item size
    // The item size is 46px (40px + 6px border) plus some gap
    // We need to calculate what distance in meters corresponds to this visual size
    // Rough estimate: the belt visual is about 800px wide, representing 1200m
    // So 46px + 20px gap = 66px visual space
    // 66px / 800px * 1200m ≈ 99 meters
    const minSpacingMeters = 50; // Items closer than 100m will overlap visually and need grouping

    // Group ALL defects that would overlap visually (before filtering)
    const groups: any[][] = [];

    if (allDefectsForDate.length > 0) {
      let currentGroup = [allDefectsForDate[0]];
      let lastGroupEndPosition = allDefectsForDate[0].position;

      for (let i = 1; i < allDefectsForDate.length; i++) {
        const defect = allDefectsForDate[i];
        const distanceFromLastItem = defect.position - lastGroupEndPosition;

        // Check if this defect would overlap
        if (distanceFromLastItem < minSpacingMeters) {
          // Add to current group
          currentGroup.push(defect);
          // Update the end position but don't move the group start
        } else {
          // Save current group and start new one
          groups.push(currentGroup);
          currentGroup = [defect];
          lastGroupEndPosition = defect.position;
        }
      }

      // Add the last group
      groups.push(currentGroup);
    }

    // Create grouped defects map
    const groupedDefects = new Map<number, any[]>();

    groups.forEach((group) => {
      // Use the first item's position for the group
      const position = group[0].position;
      groupedDefects.set(position, group);
    });

    // Create defect items for the belt map (with filtering applied)
    const defectItems: any[] = [];

    groupedDefects.forEach((defectsAtPosition, position) => {
      // Filter the defects in this group/position by health status
      const filteredDefects = defectsAtPosition.filter((d) => healthFilters.has(d.severity));

      // Skip this position entirely if no defects match the filter
      if (filteredDefects.length === 0) {
        return;
      }

      if (defectsAtPosition.length > 1) {
        // This is a grouped position - show filtered items only
        const statusPriority: Record<string, number> = {
          critical: 0,
          alarm: 1,
          monitor: 2,
          healthy: 3,
        };

        let worstStatus = 'healthy';
        let worstPriority = 3;

        filteredDefects.forEach((defect) => {
          const priority = statusPriority[defect.severity] ?? 3;
          if (priority < worstPriority) {
            worstPriority = priority;
            worstStatus = defect.severity;
          }
        });

        const group = (mockData.defectGroups as any[]).find((g) => g.position === position);
        defectItems.push({
          id: (group?.id as string | undefined) || `group-${position}`,
          type: filteredDefects[0].code,
          position,
          count: filteredDefects.length,
          status: worstStatus,
          items: filteredDefects.map((d) => ({
            id: d.id,
            type: d.code,
            status: d.severity,
            name: d.name,
            positionLabel: `${d.position} m`,
            nextSplice: `${d.nextSpliceDistance} m`,
            detectionImage: d.images.detection,
            afterImage: d.images.reference,
          })),
        });
      } else {
        // Single defect (already filtered)
        const defect = filteredDefects[0];
        defectItems.push({
          id: defect.id,
          type: defect.code,
          position: defect.position,
          status: defect.severity,
          name: defect.name,
          positionLabel: `${defect.position} m`,
          nextSplice: `${defect.nextSpliceDistance} m`,
          detectionImage: defect.images.detection,
          afterImage: defect.images.reference,
        });
      }
    });

    return defectItems.sort((a, b) => a.position - b.position);
  }, [selectedDate, healthFilters]);

  // Handle selection when date changes or on initial load
  useEffect(() => {
    if (beltDefects.length > 0) {
      if (!hasInitialized) {
        // First time initialization - select the first item
        const firstDefect = beltDefects[0];
        if (firstDefect.items && firstDefect.items.length > 0) {
          setSelectedDefectId(firstDefect.items[0].id);
        } else {
          setSelectedDefectId(firstDefect.id);
        }
        setHasInitialized(true);
      } else if (selectedDefectId) {
        // Date changed - try to keep the same selected item
        const allDefectsFlat: any[] = [];
        beltDefects.forEach((item) => {
          if (item.items && item.items.length > 0) {
            item.items.forEach((subItem: any) => {
              allDefectsFlat.push(subItem);
            });
          } else {
            allDefectsFlat.push(item);
          }
        });

        // Check if currently selected item exists in new date's data
        const selectedStillExists = allDefectsFlat.find((d) => d.id === selectedDefectId);

        if (!selectedStillExists) {
          // If selected item doesn't exist on this date, keep the selection empty
          // This way the user knows their selected item doesn't exist on this date
          // Alternatively, we could select the first item:
          // const firstDefect = beltDefects[0];
          // if (firstDefect.items && firstDefect.items.length > 0) {
          //   setSelectedDefectId(firstDefect.items[0].id);
          // } else {
          //   setSelectedDefectId(firstDefect.id);
          // }
        }
        // Otherwise keep the current selection
      }
    } else {
      // No defects for this date, clear selection
      setSelectedDefectId('');
    }
  }, [selectedDate, beltDefects]);

  // Get ALL defects for the date (unfiltered) for navigation and health chart
  const allDefectsUnfiltered = useMemo(() => {
    // Get the date string for comparison
    const dateStr = selectedDate.split('T')[0];

    // Collect ALL defects for the date (without health filter)
    const defectsForDate: any[] = [];

    mockData.defects.forEach((defect) => {
      const historicalData = mockData.historicalData[defect.id as keyof typeof mockData.historicalData];

      if (!historicalData || historicalData.length === 0) {
        return;
      }

      const dateEntry = historicalData.find((h) => h.date === dateStr);
      if (!dateEntry) {
        return;
      }

      defectsForDate.push({
        id: defect.id,
        type: defect.code,
        name: defect.name,
        status: dateEntry.severity,
        position: defect.position,
        positionLabel: `${defect.position} m`,
        nextSplice: `${defect.nextSpliceDistance} m`,
        detectionImage: defect.images.detection,
        afterImage: defect.images.reference,
      });
    });

    return defectsForDate;
  }, [selectedDate]);

  // Get all defects in a flat array for navigation (from filtered belt defects)
  const allDefects = useMemo(() => {
    const flatDefects: any[] = [];

    beltDefects.forEach((item) => {
      if (item.items && item.items.length > 0) {
        // Add individual items from the group
        item.items.forEach((subItem: any) => {
          flatDefects.push({
            ...subItem,
            position: item.position,
          });
        });
      } else {
        flatDefects.push(item);
      }
    });

    return flatDefects;
  }, [beltDefects]);

  // Find selected defect from filtered list (for navigation)
  const selectedItemIndex = allDefects.findIndex((d) => d.id === selectedDefectId);
  const selectedItem = selectedItemIndex >= 0 ? allDefects[selectedItemIndex] : undefined;

  // Find selected defect from unfiltered list (for health chart)
  const selectedItemUnfiltered = allDefectsUnfiltered.find((d) => d.id === selectedDefectId);

  // Update Redux when selected date or defect changes
  useEffect(() => {
    dispatch(setBeltDefectsSelectedDate(selectedDate));

    // Get health status for selected defect on selected date
    if (selectedItemUnfiltered) {
      const dateStr = selectedDate.split('T')[0];
      const defect = mockData.defects.find((d) => d.id === selectedItemUnfiltered.id);
      if (defect) {
        const historicalData = mockData.historicalData[defect.id as keyof typeof mockData.historicalData];
        if (historicalData) {
          const dateEntry = historicalData.find((h: any) => h.date === dateStr);
          if (dateEntry) {
            dispatch(setBeltDefectsSelectedDefectHealth(dateEntry.severity));
          }
        }
      }
    }
  }, [selectedDate, selectedItemUnfiltered, dispatch]);

  const handlePreviousItem = () => {
    if (allDefects.length === 0) return;

    // LEFT arrow - move FORWARD in counter-clockwise direction
    // This means going to the NEXT item in the array
    const newIndex = selectedItemIndex >= allDefects.length - 1 ? 0 : selectedItemIndex + 1;
    setSelectedDefectId(allDefects[newIndex].id);
  };

  const handleNextItem = () => {
    if (allDefects.length === 0) return;

    // RIGHT arrow - move BACKWARD in counter-clockwise direction
    // This means going to the PREVIOUS item in the array
    const newIndex = selectedItemIndex <= 0 ? allDefects.length - 1 : selectedItemIndex - 1;
    setSelectedDefectId(allDefects[newIndex].id);
  };

  const handleDefectClick = (id: string) => {
    setSelectedDefectId(id);
  };

  const isDateClickEnabled = false;

  const handleDateClick = (date: string) => {
    if (!isDateClickEnabled) return;
    setSelectedDate(date);
  };

  const handleFilterChange = (filters: Set<string>) => {
    // Ensure at least one filter is selected
    if (filters.size > 0) {
      setHealthFilters(filters);
    }
  };

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle arrow keys if there are defects to navigate
      if (allDefects.length === 0) return;

      const currentIndex = allDefects.findIndex((d) => d.id === selectedDefectId);

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        // LEFT arrow - move FORWARD in counter-clockwise direction
        const newIndex = currentIndex >= allDefects.length - 1 ? 0 : currentIndex + 1;
        setSelectedDefectId(allDefects[newIndex].id);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        // RIGHT arrow - move BACKWARD in counter-clockwise direction
        const newIndex = currentIndex <= 0 ? allDefects.length - 1 : currentIndex - 1;
        setSelectedDefectId(allDefects[newIndex].id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedDefectId, allDefects]);

  const formatDateDisplay = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }).replace(/\//g, '.');
  };

  return (
    <div className="flex flex-col gap-2 w-full h-full overflow-hidden bg-gray-50">
      {/* Top Section */}
      <div className="flex-1 min-h-0 overflow-auto">
        <TopSection
          beltDefects={beltDefects}
          selectedDefectId={selectedDefectId}
          onDefectClick={handleDefectClick}
          beltConfig={mockData.belt.config}
          allDefects={allDefects}
          selectedItem={selectedItem}
          handlePreviousItem={handlePreviousItem}
          handleNextItem={handleNextItem}
          healthFilters={healthFilters}
          handleFilterChange={handleFilterChange}
          lastInspectionDate={lastInspectionDate}
          formatDateDisplay={formatDateDisplay}
        />
      </div>

      {/* Bottom Section - Health Trend Chart */}
      <div className="flex-shrink-0">
        {selectedItemUnfiltered && (
          <BeltHealthTrend
            selectedItemId={selectedItemUnfiltered.id}
            selectedItemName={selectedItemUnfiltered.name}
            onDateClick={handleDateClick}
            currentDate={selectedDate}
            historicalData={mockData.historicalData}
            inspectionDates={mockData.inspectionDates}
            isClickEnabled={isDateClickEnabled}
          />
        )}
      </div>
    </div>
  );
}
